import React, { useState, useEffect } from 'react';
import {Layout} from '../../../widgets/Layout';
import { useAuth } from '../../../features/auth';
import { useNavigate } from 'react-router-dom';
import {commonStyles, theme} from '../../../shared/lib/styles';
import {userAPI} from '../../../shared/api/api';

const formatAccessExpiresInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatAccessExpiresDisplay = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
};

const SettingsPage = () => {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
    });

    const [newUserForm, setNewUserForm] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
        is_active: true,
        access_days: 30,
        access_expires_at: '',
    });

    const [activeTab, setActiveTab] = useState('profile');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [editUserForm, setEditUserForm] = useState({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
        is_active: true,
        access_days: '',
        access_expires_at: '',
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                email: user.email || '',
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                password: '',
                password_confirm: '',
            });
        }
    }, [user]);

    // Загружаем список пользователей при открытии вкладки "Пользователи"
    useEffect(() => {
        if (activeTab === 'users' && (user?.is_staff || user?.is_superuser)) {
            loadUsers();
        }
    }, [activeTab, user]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleNewUserChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewUserForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setErrors({});
        setMessage('');

        // Валидация обязательных полей
        if (!formData.username.trim()) {
            setErrors({username: 'Имя пользователя обязательно'});
            return;
        }

        // Если указан пароль, требуется подтверждение
        if (formData.password && !formData.password_confirm) {
            setErrors({password_confirm: 'Требуется подтверждение пароля'});
            return;
        }

        if (formData.password && formData.password !== formData.password_confirm) {
            setErrors({password: 'Пароли не совпадают'});
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                username: formData.username,
                email: formData.email || '',
                first_name: formData.first_name || '',
                last_name: formData.last_name || '',
            };

            if (formData.password) {
                updateData.password = formData.password;
                updateData.password_confirm = formData.password_confirm;
            }

            // Используем id или user_id в зависимости от того, что есть
            const userId = user.id || user.user_id;
            if (!userId) {
                setErrors({general: 'Не удалось определить ID пользователя'});
                setLoading(false);
                return;
            }
            
            const result = await userAPI.update(userId, updateData);
            if (result.success) {
                // Проверяем, изменился ли username или password
                const usernameChanged = formData.username !== (user?.username || '');
                const passwordChanged = !!formData.password;
                
                if (usernameChanged || passwordChanged) {
                    // Если изменились учетные данные, разлогиниваем пользователя
                    setMessage('Профиль успешно обновлён! Пожалуйста, войдите снова.');
                    setTimeout(() => {
                        logout();
                        navigate('/login');
                    }, 1500);
                } else {
                    setMessage('Профиль успешно обновлён!');
                    setFormData(prev => ({...prev, password: '', password_confirm: ''}));
                    if (updateUser && result.data) {
                        // Обновляем данные пользователя в контексте
                        const userData = result.data.data || result.data;
                        updateUser(userData);
                    }
                }
            } else {
                // Обработка ошибок от API
                if (typeof result.error === 'object') {
                    setErrors(result.error);
                } else {
                    setErrors({general: result.error || 'Ошибка при обновлении профиля'});
                }
            }
        } catch (error) {
            setErrors({general: 'Ошибка при обновлении профиля'});
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setErrors({});
        setMessage('');

        // Валидация обязательных полей
        if (!newUserForm.username.trim()) {
            setErrors({username: 'Имя пользователя обязательно'});
            return;
        }

        if (!newUserForm.password.trim()) {
            setErrors({password: 'Пароль обязателен'});
            return;
        }

        if (!newUserForm.password_confirm.trim()) {
            setErrors({password_confirm: 'Подтверждение пароля обязательно'});
            return;
        }

        if (newUserForm.password !== newUserForm.password_confirm) {
            setErrors({password: 'Пароли не совпадают'});
            return;
        }

        setLoading(true);
        try {
            const createData = {
                username: newUserForm.username,
                password: newUserForm.password,
                password_confirm: newUserForm.password_confirm,
                email: newUserForm.email || '',
                first_name: newUserForm.first_name || '',
                last_name: newUserForm.last_name || '',
                is_active: newUserForm.is_active,
            };
            if (newUserForm.access_expires_at) {
                createData.access_expires_at = new Date(newUserForm.access_expires_at).toISOString();
            } else if (newUserForm.access_days) {
                createData.access_days = Number(newUserForm.access_days);
            }

            const result = await userAPI.create(createData);
            if (result.success) {
                setMessage(`Пользователь ${newUserForm.username} успешно создан!`);
                setNewUserForm({ 
                    username: '', 
                    email: '', 
                    first_name: '',
                    last_name: '',
                    password: '', 
                    password_confirm: '',
                    is_active: true,
                    access_days: 30,
                    access_expires_at: '',
                });
                setShowCreateForm(false);
                // Обновляем список пользователей
                await loadUsers();
            } else {
                // Обработка ошибок от API
                if (typeof result.error === 'object') {
                    setErrors(result.error);
                } else {
                    setErrors({general: result.error || 'Ошибка при создании пользователя'});
                }
            }
        } catch (error) {
            setErrors({general: 'Ошибка при создании пользователя'});
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const result = await userAPI.getAll();
            if (result.success) {
                setUsers(result.data?.data || []);
            }
        } catch (error) {
            console.error('Ошибка при загрузке пользователей:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (userItem) => {
        // При одинарном клике только выбираем пользователя для показа кнопки "Удалить"
        setSelectedUser(userItem);
    };

    const handleUserDoubleClick = (userItem) => {
        // При двойном клике открываем модальное окно редактирования
        setSelectedUser(userItem);
        setEditUserForm({
            username: userItem.username || '',
            email: userItem.email || '',
            first_name: userItem.first_name || '',
            last_name: userItem.last_name || '',
            password: '',
            password_confirm: '',
            is_active: userItem.is_active !== undefined ? userItem.is_active : true,
            access_days: '',
            access_expires_at: formatAccessExpiresInput(userItem.access_expires_at),
        });
        setShowEditModal(true);
        setShowCreateForm(false);
        setErrors({});
        setMessage('');
    };

    const handleCreateButtonClick = () => {
        setSelectedUser(null);
        // Явно очищаем все поля формы
        setNewUserForm({
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            password_confirm: '',
            is_active: true,
            access_days: 30,
            access_expires_at: '',
        });
        setShowCreateForm(true);
        setShowEditModal(false);
        setErrors({});
        setMessage('');
    };

    const handleEditButtonClick = () => {
        if (!selectedUser) {
            return;
        }
        setEditUserForm({
            username: selectedUser.username || '',
            email: selectedUser.email || '',
            first_name: selectedUser.first_name || '',
            last_name: selectedUser.last_name || '',
            password: '',
            password_confirm: '',
            is_active: selectedUser.is_active !== undefined ? selectedUser.is_active : true,
            access_days: '',
            access_expires_at: formatAccessExpiresInput(selectedUser.access_expires_at),
        });
        setShowEditModal(true);
        setShowCreateForm(false);
        setErrors({});
        setMessage('');
    };

    // Вспомогательная функция для рендеринга поля формы
    const renderFormField = (name, label, formData, onChange, options = {}) => {
        const {
            type = 'text',
            required = false,
            placeholder = '',
            autoComplete = undefined,
            showRequired = required
        } = options;
        
        return (
            <div style={pageStyles.inputGroup}>
                <label>
                    {label} {showRequired && <span style={{color: theme.colors.danger}}>*</span>}
                </label>
                <input
                    name={name}
                    type={type}
                    value={formData[name] || ''}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    autoComplete={autoComplete}
                    style={pageStyles.input}
                />
                {errors[name] && <div style={commonStyles.errorText}>{errors[name]}</div>}
            </div>
        );
    };

    // Вспомогательная функция для рендеринга модального окна
    const renderUserModal = (title, formData, onChange, onSubmit, submitText, isEdit = false) => {
        const isOpen = isEdit ? showEditModal : showCreateForm;
        if (!isOpen) return null;

        return (
            <div style={pageStyles.modalOverlay} onClick={handleCloseModal}>
                <div style={pageStyles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div style={pageStyles.modalHeader}>
                        <h2 style={pageStyles.modalTitle}>{title}</h2>
                        <button onClick={handleCloseModal} style={pageStyles.modalCloseButton}>×</button>
                    </div>
                    
                    <form onSubmit={onSubmit} style={pageStyles.form}>
                        {message && <div style={commonStyles.success}>{message}</div>}
                        {errors.general && <div style={commonStyles.error}>{errors.general}</div>}
                        
                        {renderFormField('username', 'Имя пользователя', formData, onChange, {
                            required: true,
                            placeholder: isEdit ? '' : 'Введите логин',
                            autoComplete: isEdit ? undefined : 'off'
                        })}
                        
                        {renderFormField('first_name', 'Имя', formData, onChange)}
                        {renderFormField('last_name', 'Фамилия', formData, onChange)}
                        
                        {renderFormField('email', 'Email', formData, onChange, {
                            type: 'email',
                            placeholder: 'user@example.com'
                        })}
                        
                        {renderFormField('password', isEdit ? 'Новый пароль (оставьте пустым, если не меняете)' : 'Пароль', formData, onChange, {
                            type: 'password',
                            required: !isEdit,
                            placeholder: isEdit ? '' : 'Введите пароль',
                            autoComplete: 'new-password',
                            showRequired: !isEdit
                        })}
                        
                        {(formData.password || !isEdit) && (
                            renderFormField('password_confirm', 'Подтверждение пароля', formData, onChange, {
                                type: 'password',
                                required: !isEdit,
                                placeholder: 'Повторите пароль',
                                autoComplete: 'new-password'
                            })
                        )}
                        
                        <div style={pageStyles.inputGroup}>
                            <label style={{display: 'flex', alignItems: 'center', gap: theme.spacing.sm}}>
                                <input
                                    name="is_active"
                                    type="checkbox"
                                    checked={formData.is_active || false}
                                    onChange={onChange}
                                />
                                Активен
                            </label>
                        </div>

                        {renderFormField(
                            isEdit ? 'access_expires_at' : 'access_days',
                            isEdit ? 'Доступ действует до' : 'Срок доступа (дней)',
                            formData,
                            onChange,
                            isEdit
                                ? { type: 'datetime-local', placeholder: '' }
                                : { type: 'number', placeholder: '30' }
                        )}
                        {!isEdit && (
                            <p style={{ fontSize: theme.fontSize.sm, color: theme.colors.textSecondary, margin: 0 }}>
                                По умолчанию 30 дней (месяц). Можно задать точную дату ниже.
                            </p>
                        )}
                        {!isEdit && renderFormField(
                            'access_expires_at',
                            'Или точная дата окончания доступа',
                            formData,
                            onChange,
                            { type: 'datetime-local' }
                        )}
                        
                        <div style={{display: 'flex', gap: theme.spacing.md, marginTop: theme.spacing.lg}}>
                            <button type="submit" disabled={loading} style={pageStyles.saveButton}>
                                {loading ? (isEdit ? 'Сохранение...' : 'Создание...') : submitText}
                            </button>
                            <button type="button" onClick={handleCloseModal} style={{...pageStyles.saveButton, backgroundColor: theme.colors.textSecondary, color: theme.colors.textPrimary}}>
                                Отмена
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) {
            return;
        }

        // Подтверждение удаления
        const confirmMessage = `Вы уверены, что хотите удалить пользователя "${selectedUser.username}"?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setLoading(true);
        setErrors({});
        setMessage('');

        try {
            const result = await userAPI.delete(selectedUser.id);
            if (result.success) {
                setMessage(`Пользователь "${selectedUser.username}" успешно удалён!`);
                setSelectedUser(null);
                // Обновляем список пользователей
                await loadUsers();
            } else {
                setErrors({general: result.error || 'Ошибка при удалении пользователя'});
            }
        } catch (error) {
            setErrors({general: 'Ошибка при удалении пользователя'});
        } finally {
            setLoading(false);
        }
    };

    const handleEditUserChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditUserForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveUser = async (e) => {
        e.preventDefault();
        setErrors({});
        setMessage('');

        if (!editUserForm.username.trim()) {
            setErrors({username: 'Имя пользователя обязательно'});
            return;
        }

        if (editUserForm.password && !editUserForm.password_confirm) {
            setErrors({password_confirm: 'Требуется подтверждение пароля'});
            return;
        }

        if (editUserForm.password && editUserForm.password !== editUserForm.password_confirm) {
            setErrors({password: 'Пароли не совпадают'});
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                username: editUserForm.username,
                email: editUserForm.email || '',
                first_name: editUserForm.first_name || '',
                last_name: editUserForm.last_name || '',
                is_active: editUserForm.is_active,
            };
            if (editUserForm.access_expires_at) {
                updateData.access_expires_at = new Date(editUserForm.access_expires_at).toISOString();
            }

            if (editUserForm.password) {
                updateData.password = editUserForm.password;
                updateData.password_confirm = editUserForm.password_confirm;
            }

            const result = await userAPI.update(selectedUser.id, updateData);
            if (result.success) {
                // Проверяем, изменился ли username или password у текущего пользователя
                const isCurrentUser = user?.id === selectedUser.id;
                const usernameChanged = editUserForm.username !== selectedUser.username;
                const passwordChanged = !!editUserForm.password;
                
                if (isCurrentUser && (usernameChanged || passwordChanged)) {
                    // Если изменились учетные данные текущего пользователя, разлогиниваем его
                    setMessage('Пользователь успешно обновлён! Пожалуйста, войдите снова.');
                    setTimeout(() => {
                        handleCloseModal();
                        logout();
                        navigate('/login');
                    }, 1500);
                } else {
                    setMessage('Пользователь успешно обновлён!');
                    setEditUserForm(prev => ({...prev, password: '', password_confirm: ''}));
                    await loadUsers(); // Обновляем список
                    // Закрываем модальное окно через небольшую задержку, чтобы показать сообщение об успехе
                    setTimeout(() => {
                        handleCloseModal();
                    }, 700);
                }
            } else {
                if (typeof result.error === 'object') {
                    setErrors(result.error);
                } else {
                    setErrors({general: result.error || 'Ошибка при обновлении пользователя'});
                }
            }
        } catch (error) {
            setErrors({general: 'Ошибка при обновлении пользователя'});
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setShowCreateForm(false);
        setSelectedUser(null);
        setEditUserForm({
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            password_confirm: '',
            is_active: true,
            access_days: '',
            access_expires_at: '',
        });
        setNewUserForm({
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            password_confirm: '',
            is_active: true,
            access_days: 30,
            access_expires_at: '',
        });
        setErrors({});
        setMessage('');
    };

    return (
        <Layout>
            <div style={pageStyles.container}>
                <h1 style={pageStyles.title}>Настройки</h1>

                <div style={pageStyles.tabs}>
                    <button
                        onClick={() => setActiveTab('profile')}
                        style={{ ...pageStyles.tabButton, ...(activeTab === 'profile' ? pageStyles.activeTab : {}) }}
                    >
                        Мой профиль
                    </button>
                    {(user?.is_staff || user?.is_superuser) && (
                        <button
                            onClick={() => setActiveTab('users')}
                            style={{ ...pageStyles.tabButton, ...(activeTab === 'users' ? pageStyles.activeTab : {}) }}
                        >
                            Пользователи
                        </button>
                    )}
                </div>

                {activeTab === 'profile' && user ? (
                    <form onSubmit={handleSaveProfile} style={pageStyles.form}>
                        {message && <div style={commonStyles.success}>{message}</div>}
                        {errors.general && <div style={commonStyles.error}>{errors.general}</div>}
                        
                        <div style={pageStyles.inputGroup}>
                            <label>Имя пользователя <span style={{color: theme.colors.danger}}>*</span></label>
                            <input
                                name="username"
                                value={formData.username}
                                onChange={handleProfileChange}
                                required
                                style={pageStyles.input}
                            />
                            {errors.username && <div style={commonStyles.errorText}>{errors.username}</div>}
                        </div>
                        <div style={pageStyles.inputGroup}>
                            <label>Имя</label>
                            <input
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleProfileChange}
                                style={pageStyles.input}
                            />
                            {errors.first_name && <div style={commonStyles.errorText}>{errors.first_name}</div>}
                        </div>
                        <div style={pageStyles.inputGroup}>
                            <label>Фамилия</label>
                            <input
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleProfileChange}
                                style={pageStyles.input}
                            />
                            {errors.last_name && <div style={commonStyles.errorText}>{errors.last_name}</div>}
                        </div>
                        <div style={pageStyles.inputGroup}>
                            <label>Email</label>
                            <input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleProfileChange}
                                style={pageStyles.input}
                            />
                            {errors.email && <div style={commonStyles.errorText}>{errors.email}</div>}
                        </div>
                        <div style={pageStyles.inputGroup}>
                            <label>Новый пароль (оставьте пустым, если не меняете)</label>
                            <input
                                name="password"
                                type="password"
                                value={formData.password}
                                onChange={handleProfileChange}
                                style={pageStyles.input}
                            />
                            {errors.password && <div style={commonStyles.errorText}>{errors.password}</div>}
                        </div>
                        {!user.access_unlimited && (
                            <div style={pageStyles.inputGroup}>
                                <label>Доступ к приложению</label>
                                <p style={{ margin: `${theme.spacing.xs} 0 0`, color: theme.colors.textSecondary }}>
                                    {user.access_expired
                                        ? 'Срок доступа истёк. Обратитесь к администратору.'
                                        : `Действует до: ${formatAccessExpiresDisplay(user.access_expires_at)}`}
                                    {user.access_days_remaining != null && !user.access_expired && (
                                        <> (осталось дней: {user.access_days_remaining})</>
                                    )}
                                </p>
                            </div>
                        )}
                        {formData.password && (
                            <div style={pageStyles.inputGroup}>
                                <label>Подтверждение пароля <span style={{color: theme.colors.danger}}>*</span></label>
                                <input
                                    name="password_confirm"
                                    type="password"
                                    value={formData.password_confirm}
                                    onChange={handleProfileChange}
                                    style={pageStyles.input}
                                />
                                {errors.password_confirm && <div style={commonStyles.errorText}>{errors.password_confirm}</div>}
                            </div>
                        )}
                        <button type="submit" disabled={loading} style={pageStyles.saveButton}>
                            {loading ? 'Сохранение...' : 'Сохранить профиль'}
                        </button>
                    </form>
                ) : activeTab === 'users' && (user?.is_staff || user?.is_superuser) ? (
                    <div>
                        {loading && <div style={commonStyles.loadingContainer}><div style={commonStyles.loadingSpinner}></div></div>}
                        {message && <div style={commonStyles.success}>{message}</div>}
                        {errors.general && <div style={commonStyles.error}>{errors.general}</div>}
                        
                        {/* Кнопки управления пользователями */}
                        <div style={{marginBottom: theme.spacing.lg, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <h2 style={{...commonStyles.titleLarge, margin: 0}}>Список пользователей</h2>
                            <div style={{display: 'flex', gap: theme.spacing.md}}>
                                <button
                                    onClick={handleCreateButtonClick}
                                    style={{...commonStyles.button, ...commonStyles.buttonPrimary}}
                                >
                                    + Создать пользователя
                                </button>
                                {selectedUser && (
                                    <>
                                        <button
                                            onClick={handleEditButtonClick}
                                            style={{...commonStyles.button, ...commonStyles.buttonPrimary}}
                                        >
                                            Редактировать
                                        </button>
                                        <button
                                            onClick={handleDeleteUser}
                                            style={{...commonStyles.button, ...commonStyles.buttonDanger}}
                                        >
                                            Удалить
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div style={pageStyles.tableWrapper}>
                            <table style={commonStyles.table}>
                                <thead>
                                    <tr>
                                        <th style={commonStyles.tableHeader}>ID</th>
                                        <th style={commonStyles.tableHeader}>Имя пользователя</th>
                                        <th style={commonStyles.tableHeader}>Имя</th>
                                        <th style={commonStyles.tableHeader}>Фамилия</th>
                                        <th style={commonStyles.tableHeader}>Email</th>
                                        <th style={commonStyles.tableHeader}>Активен</th>
                                        <th style={commonStyles.tableHeader}>Доступ до</th>
                                        <th style={commonStyles.tableHeader}>Дата регистрации</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((userItem) => (
                                        <tr 
                                            key={userItem.id} 
                                            onClick={() => handleUserClick(userItem)}
                                            onDoubleClick={() => handleUserDoubleClick(userItem)}
                                            style={{
                                                ...pageStyles.tableRow, 
                                                cursor: 'pointer',
                                                backgroundColor: selectedUser?.id === userItem.id ? theme.colors.bgLight : 'transparent',
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedUser?.id !== userItem.id) {
                                                    e.currentTarget.style.backgroundColor = theme.colors.bgLight;
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedUser?.id !== userItem.id) {
                                                    e.currentTarget.style.backgroundColor = 'transparent';
                                                }
                                            }}
                                        >
                                            <td style={commonStyles.tableCell}>{userItem.id}</td>
                                            <td style={commonStyles.tableCell}>{userItem.username}</td>
                                            <td style={commonStyles.tableCell}>{userItem.first_name || '-'}</td>
                                            <td style={commonStyles.tableCell}>{userItem.last_name || '-'}</td>
                                            <td style={commonStyles.tableCell}>{userItem.email || '-'}</td>
                                            <td style={commonStyles.tableCell}>{userItem.is_active ? 'Да' : 'Нет'}</td>
                                            <td style={commonStyles.tableCell}>
                                                {userItem.access_unlimited
                                                    ? 'Без ограничения'
                                                    : formatAccessExpiresDisplay(userItem.access_expires_at)}
                                            </td>
                                            <td style={commonStyles.tableCell}>{userItem.date_joined ? new Date(userItem.date_joined).toLocaleDateString('ru-RU') : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {users.length === 0 && !loading && (
                                <div style={{textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary}}>
                                    Пользователи не найдены
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {/* Модальное окно для редактирования пользователя */}
                {renderUserModal(
                    'Редактировать пользователя',
                    editUserForm,
                    handleEditUserChange,
                    handleSaveUser,
                    'Сохранить',
                    true
                )}

                {/* Модальное окно для создания пользователя */}
                {renderUserModal(
                    'Создать пользователя',
                    newUserForm,
                    handleNewUserChange,
                    handleCreateUser,
                    'Создать',
                    false
                )}

                <div style={pageStyles.footerActions}>
                    <button onClick={handleLogout} style={pageStyles.logoutButton}>
                        Выйти из аккаунта
                    </button>
                </div>
            </div>
        </Layout>
    );
};

const pageStyles = {
    container: {
        ...commonStyles.container,
        maxWidth: '1200px',
    },
    title: {
        ...commonStyles.titleLarge,
        marginBottom: theme.spacing.lg,
    },
    tabs: commonStyles.tabs,
    tabButton: commonStyles.tabButton,
    activeTab: commonStyles.tabButtonActive,
    form: commonStyles.form,
    inputGroup: commonStyles.inputGroup,
    input: commonStyles.input,
    saveButton: {
        ...commonStyles.button,
        ...commonStyles.buttonPrimary,
        marginTop: theme.spacing.sm,
    },
    footerActions: commonStyles.footerActions,
    logoutButton: {
        ...commonStyles.button,
        ...commonStyles.buttonDanger,
    },
    tableWrapper: {
        ...commonStyles.tableWrapper,
        marginTop: theme.spacing.lg,
    },
    tableRow: {
        transition: `background-color ${theme.transitions.default}`,
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modalContent: {
        backgroundColor: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.xl,
        maxWidth: '600px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: theme.shadows.xl,
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.lg,
        paddingBottom: theme.spacing.md,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
    },
    modalTitle: {
        ...commonStyles.titleLarge,
        margin: 0,
    },
    modalCloseButton: {
        background: 'none',
        border: 'none',
        fontSize: '2rem',
        cursor: 'pointer',
        color: theme.colors.textSecondary,
        padding: 0,
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
    },
};

export default SettingsPage;

